import { Boss, type BossDef } from './boss-base';
import { GAME } from '@/config/game';
import { getTexture } from '@/assets/resolver';
import type { BulletSystem } from '@/entities/bullets';
import type { TelegraphSink } from '@/render/telegraphs';
import { BOSS_FEEDBACK } from '@/config/boss-feedback';

/**
 * B8 — Granite Fist (earth Elemental). Sheathed in rock
 * armor that shrugs off every hit ("clink") — it is ONLY vulnerable in the
 * brief STUN after it SLAMS the ground. The slam is telegraphed by cracks
 * radiating across the floor, then erupts as a seismic shockwave ring plus
 * a rain of falling boulders. docs/02 Elementals.
 * Signatures: armor/slam/stun cycle (always), aimed rock fan (P1/P2), rock
 * ring (P3).
 */
export const TERRA: BossDef = {
  id: 'terra',
  nameKey: 'boss.terra',
  tier: 4,
  minRoom: 3,
  baseHp: 1600,
  radius: 50,
  bodyKey: 'boss/terra/body',
  movement: 'orbit',
  contactDamage: 1,
  phases: [
    {
      hpPct: 1.0,
      patterns: [
        {
          // Rolling boulders: lobbed slow, they gather speed (B3 accel)
          type: 'aimed', bullet: 'bullet/enemy/rock', count: 3, spreadDeg: 24,
          interval: 2.8, telegraph: 0.5, speed: 110, radius: 11, damage: 1,
          motion: { accel: 240, maxSpeed: 290 },
        },
      ],
    },
    {
      hpPct: 0.6,
      patterns: [
        {
          type: 'aimed', bullet: 'bullet/enemy/rock', count: 3, spreadDeg: 24,
          interval: 2.6, telegraph: 0.5, speed: 210, radius: 11, damage: 1,
        },
        {
          type: 'radial', bullet: 'bullet/enemy/rock', count: 9, interval: 3.6,
          spinDeg: 16, speed: 120, radius: 11, damage: 1, unlockTier: 1,
        },
      ],
    },
    {
      hpPct: 0.3,
      speedMul: 1.2,
      patterns: [
        {
          type: 'radial', bullet: 'bullet/enemy/rock', count: 11, interval: 3.2,
          spinDeg: 20, speed: 135, radius: 11, damage: 1,
        },
      ],
    },
  ],
  evolve: [
    {
      // Pebble ring: loose gravel shed while it stalks
      tier: 2,
      pattern: {
        type: 'radial', bullet: 'bullet/enemy/rock', count: 8, interval: 4.4,
        spinDeg: 22, speed: 110, radius: 10, damage: 1,
      },
    },
    {
      // Twin hurl: a telegraphed double boulder fan once the shell cracks
      tier: 3,
      fromPhase: 1,
      pattern: {
        type: 'aimed', bullet: 'bullet/enemy/rock', count: 2, spreadDeg: 16,
        interval: 3.6, telegraph: 0.5, burst: 2, burstGap: 0.3,
        speed: 230, radius: 12, damage: 1,
      },
    },
  ],
};

// The armor really blocks now (it used to take damage anyway), so the
// cycle leans toward the stun: ~35% exposed at tier 0, ~45% by tier 2
const ARMOR_TIME = 3.2; // sealed, untouchable
const WIND_TIME = 1.1; // crack telegraph before the slam
const STUN_TIME = 3.6; // exposed, takes damage
const SHOCK_COUNT = 18; // seismic ring bullets
const SHOCK_SPEED = 165;
const CRACK_SPOKES = 7;
const CRACK_LENGTH = 520; // fissure reach — the slam ring's danger radius
const RAIN_ROCKS = 7;
const RAIN_SPEED = 150;
const RAIN_INSET = 90; // rain spans the real arena, this far inside the walls
const CLINK_FLASH = 0.08;

type TerraState = 'armored' | 'winding' | 'stunned';

export class TerraBoss extends Boss {
  private state: TerraState = 'armored';
  private timer = ARMOR_TIME;

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(TERRA, level, x, y, homeX, homeY);
    this.allowReinforce = false; // the armor/stun cycle is its gate
  }

  override get isVulnerable(): boolean {
    return super.isVulnerable && this.state === 'stunned';
  }

  /** Rock armor: everything clinks off unless it is dazed after a slam. */
  override takeDamage(amount: number): boolean {
    if (this.state !== 'stunned') {
      this.hitFlash = CLINK_FLASH;
      return false;
    }
    return super.takeDamage(amount);
  }

  override update(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    super.update(dt, px, py, bullets, telegraphs);
    if (this.dead) return;

    this.timer -= dt;
    if (this.timer > 0) return;

    switch (this.state) {
      case 'armored': {
        // Rear up and crack the floor — telegraph the incoming slam
        this.state = 'winding';
        this.timer = WIND_TIME;
        if (telegraphs) {
          for (let i = 0; i < CRACK_SPOKES; i++) {
            const a = (Math.PI * 2 * i) / CRACK_SPOKES + this.timer;
            telegraphs.crack(this.x, this.y, Math.cos(a), Math.sin(a), CRACK_LENGTH, WIND_TIME);
          }
        }
        break;
      }
      case 'winding': {
        // SLAM: seismic shockwave ring + boulder rain, then it's dazed
        this.slam(bullets);
        this.state = 'stunned';
        this.timer = STUN_TIME;
        this.events?.burst(this.x, this.y, BOSS_FEEDBACK.slam);
        break;
      }
      case 'stunned': {
        this.state = 'armored';
        this.timer = ARMOR_TIME / this.intensity;
        break;
      }
    }
  }

  private slam(bullets: BulletSystem): void {
    for (let i = 0; i < SHOCK_COUNT; i++) {
      const a = (Math.PI * 2 * i) / SHOCK_COUNT;
      bullets.spawn({
        x: this.x, y: this.y, dirX: Math.cos(a), dirY: Math.sin(a),
        speed: SHOCK_SPEED * this.bulletSpeedMul, life: 4, radius: 11, damage: 2,
        friendly: false, texKey: 'bullet/enemy/rock', rotates: false,
      });
    }
    // Boulder rain from the top edge, spread across the REAL arena (X3)
    const inset = GAME.arena.wallThickness + RAIN_INSET;
    const speed = RAIN_SPEED * this.bulletSpeedMul;
    const life = (this.arenaH - inset * 2) / speed;
    for (let i = 0; i < RAIN_ROCKS; i++) {
      const x = inset + ((this.arenaW - inset * 2) * i) / (RAIN_ROCKS - 1);
      bullets.spawn({
        x, y: inset, dirX: 0, dirY: 1,
        speed, life, radius: 12, damage: 2,
        friendly: false, texKey: 'bullet/enemy/rock', rotates: false,
      });
    }
  }

  /** Earth boss only drifts while sealed; it plants itself to slam. */
  protected override move(dt: number, px: number, py: number): void {
    if (this.state === 'armored') super.move(dt, px, py);
  }

  override render(alpha: number): void {
    super.render(alpha);
    this.body.texture = getTexture('boss/terra/body', this.state === 'stunned' ? 1 : 0);
  }
}
